import React from "react";
import moment from "moment";
import "./ReportSummaryCards.css";

const EXPECTED_SUBMIT_TIME_MINUTES = 19 * 60;
const CHECK_TIME_FORMAT = "YYYY-MM-DD hh:mm:ss A Z";

const parseCheckTime = (datetime) => {
  if (!datetime) return null;
  const parsed = moment(datetime, CHECK_TIME_FORMAT);
  return parsed.isValid() ? parsed : null;
};

const formatTime = (datetime) => {
  const parsed = parseCheckTime(datetime);
  return parsed ? parsed.format("hh:mm A") : "--:--";
};

const formatWorkingHours = (hoursStr) => {
  if (!hoursStr) return "0h 0m";
  const [hours, minutes] = hoursStr.split(":");
  return `${parseInt(hours, 10) || 0}h ${parseInt(minutes, 10) || 0}m`;
};

const formatDateLabel = (datetime) => {
  const parsed = parseCheckTime(datetime);
  return parsed ? parsed.format("ddd, MMM D") : "--";
};

const isOnTime = (datetime) => {
  const parsed = parseCheckTime(datetime);
  if (!parsed) return null;
  const minutesOfDay = parsed.hours() * 60 + parsed.minutes();
  return minutesOfDay <= EXPECTED_SUBMIT_TIME_MINUTES;
};

/**
 * @param {boolean} submittedToday
 * @param {string} todayWorkingHours
 * @param {string} checkInTime
 * @param {string} checkOutTime
 */
function ReportSummaryCards({
  submittedToday,
  todayWorkingHours,
  checkInTime,
  checkOutTime,
  lastCheckOutTime,
}) {
  // ---------- Card 1: Today's Report ----------
  const isSubmitted = Boolean(submittedToday);
  const submittedValue = isSubmitted ? "Submitted" : "Not Yet";
  const submittedSubtitle = isSubmitted
    ? checkOutTime
      ? `at ${formatTime(checkOutTime)}`
      : "Submitted"
    : "Pending for today";

  // ---------- Card 2: Today Working Hours ----------
  const workingHoursValue = todayWorkingHours
    ? formatWorkingHours(todayWorkingHours)
    : "0h 0m";
  const workingHoursSubtitle = todayWorkingHours ? "Work Hours Logged" : "Work Hours Not Logged";

  // ---------- Card 3: Last Submission ----------
  const hasLastSubmission = Boolean(lastCheckOutTime);
  const lastSubmissionValue = hasLastSubmission
    ? formatTime(lastCheckOutTime)
    : "No submissions yet";
  const lastSubmissionSubtitle = hasLastSubmission
    ? `${formatDateLabel(lastCheckOutTime)} \u00b7`
    : "No submissions yet";

  const cards = [
    {
      key: "submitted",
      label: "Today's Report",
      value: submittedValue,
      subtitle: submittedSubtitle,
      showCheck: isSubmitted,
    },
    {
      key: "hours",
      label: "Today Working Hours",
      value: workingHoursValue,
      subtitle: workingHoursSubtitle,
    },
    {
      key: "last",
      label: "Last Submission",
      value: lastSubmissionValue,
      subtitle: lastSubmissionSubtitle,
    },
  ];

  return (
    <div className="report-summary-cards">
      {cards.map((card) => (
        <div className="report-summary-card" key={card.key}>
          <div className="report-summary-card__label">{card.label}</div>
          <div className="report-summary-card__value">
            {card.value}
            {card.showCheck && (
              <svg
                className="report-summary-card__check"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 12.5l2.5 2.5L16 9.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <div className="report-summary-card__subtitle">{card.subtitle}</div>
        </div>
      ))}
    </div>
  );
}

export default ReportSummaryCards;
